/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Approval flows", description = "Require approval before changes to models and syncs are published.", lastUpdatedDate = new Date(1790553600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Approval flows",
        description: "Require approval before changes to models and syncs are published.",
        lastUpdatedDate: "Mon Sep 28 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Getting started",
          "depth": 0,
          "id": "getting-started"
        }, {
          "value": "Require approval for a user",
          "depth": 1,
          "id": "require-approval-for-a-user"
        }, {
          "value": "Who can publish directly",
          "depth": 1,
          "id": "who-can-publish-directly"
        }, {
          "value": "Create new resources",
          "depth": 0,
          "id": "create-new-resources"
        }, {
          "value": "Edit existing resources",
          "depth": 0,
          "id": "edit-existing-resources"
        }, {
          "value": "Approve requests",
          "depth": 0,
          "id": "approve-requests"
        }, {
          "value": "Who can approve requests",
          "depth": 1,
          "id": "who-can-approve-requests"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      strong: "strong",
      img: "img",
      h2: "h2",
      ol: "ol",
      li: "li",
      code: "code",
      ul: "ul",
      h3: "h3",
      h4: "h4",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td"
    }, _provideComponents(), props.components), {BtOnly} = _components;
    if (!BtOnly) _missingMdxReference("BtOnly", true);
    return _jsxs(_Fragment, {
      children: [_jsx(BtOnly, {
        featureName: "Approval flows",
        plural: true
      }), "\n", _jsxs(_components.p, {
        children: ["Approval flows let you stage new resources (", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "models"
        }), " and ", _jsx(_components.a, {
          href: "/getting-started/concepts#syncs",
          children: "syncs"
        }), ") and changes to existing resources in ", _jsx(_components.strong, {
          children: "draft mode"
        }), " until an approver reviews them."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/permissions/approval-flow.png",
          alt: "Approval flow diagram"
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Approval flows apply only to ", _jsx(_components.strong, {
            children: "models"
          }), " and ", _jsx(_components.strong, {
            children: "syncs"
          }), ". Audiences are published\nimmediately. ", _jsx(_components.a, {
            href: "/customer-studio/journeys",
            children: "Journeys"
          }), " and Native Delivery\ncampaigns have their own ", _jsx(_components.strong, {
            children: "Journeys"
          }), " and ", _jsx(_components.strong, {
            children: "Campaigns"
          }), " toggles under\n", _jsx(_components.strong, {
            children: "Require approvals"
          }), ", with separate settings from this approval flow."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "getting-started",
        children: "Getting started"
      }), "\n", _jsx(_components.p, {
        children: "To enable approval flows:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.a, {
            href: "https://app.hightouch.com/settings?from=docs",
            children: _jsx(_components.strong, {
              children: "Settings"
            })
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Open the ", _jsx(_components.strong, {
            children: "Workspace"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Require approvals"
          }), ", turn on ", _jsx(_components.strong, {
            children: "Syncs & Models"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Save"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/permissions/require-approvals-toggle.png",
          alt: "Require approvals in Workspace settings, with the Syncs & Models toggle turned on"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["After you enable approval flows, any role with the ", _jsx(_components.code, {
          children: "Draft models & syncs"
        }), " permission can save changes as drafts or submit them for approval — not just the built-in ", _jsx(_components.strong, {
          children: "Workspace draft editor"
        }), " role."]
      }), "\n", _jsx(_components.p, {
        children: "Whether a user must get approval depends on their role:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Users with draft-only roles must submit changes for approval before publishing."
        }), "\n", _jsxs(_components.li, {
          children: ["Users with publishing permissions can submit changes for approval or use ", _jsx(_components.strong, {
            children: "Publish now"
          }), " to publish directly."]
        }), "\n", _jsxs(_components.li, {
          children: ["Users with the ", _jsx(_components.code, {
            children: "Approve models & syncs"
          }), " permission can review, approve, decline, or withdraw approval requests."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "require-approval-for-a-user",
        children: "Require approval for a user"
      }), "\n", _jsx(_components.p, {
        children: "To require a user's model and sync changes to go through approval, assign them a role that enforces drafts."
      }), "\n", _jsx(_components.h4, {
        id: "use-the-workspace-draft-editor-role",
        children: "Use the Workspace draft editor role"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Settings > Members"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Find the user."
        }), "\n", _jsxs(_components.li, {
          children: ["Change their role to ", _jsx(_components.strong, {
            children: "Workspace draft editor"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Users with this role can create and edit models and syncs, but their changes must be approved before they are published."
      }), "\n", _jsx(_components.h4, {
        id: "create-a-custom-draft-role",
        children: "Create a custom draft role"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Settings > Roles"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/workspace-management/roles#custom-roles",
            children: "Create a custom role"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Open the ", _jsx(_components.strong, {
            children: "Sources"
          }), " or ", _jsx(_components.strong, {
            children: "Destinations"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.code, {
            children: "Draft models & syncs"
          }), " for the relevant resources."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Users with this permission can draft changes but do not see the ", _jsx(_components.strong, {
          children: "Publish now"
        }), " option."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/permissions/draft-contributor-permissions.png",
          alt: "Custom role builder showing Draft models & syncs and Approve models & syncs permissions for sources"
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/permissions/approvals-destinations-draft-permissions.png",
          alt: "Custom role builder showing Draft models & syncs enabled for destinations"
        })
      }), "\n", _jsx(_components.h3, {
        id: "who-can-publish-directly",
        children: "Who can publish directly"
      }), "\n", _jsxs(_components.p, {
        children: ["Some roles can bypass approval and publish directly. For example, users with the ", _jsx(_components.strong, {
          children: "Admin"
        }), " or ", _jsx(_components.strong, {
          children: "Workspace editor"
        }), " role can save drafts, submit approval requests, or select ", _jsx(_components.strong, {
          children: "Publish now"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Any role without the ", _jsx(_components.code, {
          children: "Draft models & syncs"
        }), " restriction can self-publish."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["The ", _jsx(_components.strong, {
            children: "Workspace draft editor"
          }), " role is the built-in role whose changes must be\napproved. It is not the only role that can create drafts. Any role with the\n", _jsx(_components.code, {
            children: "Draft models & syncs"
          }), " permission can create and edit drafts. Roles that can\npublish directly can also save drafts voluntarily but still have access to\n", _jsx(_components.strong, {
            children: "Publish now"
          }), "."]
        })
      }), "\n", _jsxs(Tip, {
        children: [_jsx(_components.p, {
          children: "If you use approval flows, we recommend the following role structure:"
        }), _jsxs(_components.ul, {
          children: ["\n", _jsxs(_components.li, {
            children: [_jsx(_components.strong, {
              children: "Admin"
            }), " — Can approve changes and manage the workspace."]
          }), "\n", _jsxs(_components.li, {
            children: [_jsx(_components.strong, {
              children: "Workspace editor"
            }), " — Can approve changes and publish directly."]
          }), "\n", _jsxs(_components.li, {
            children: [_jsx(_components.strong, {
              children: "Workspace draft editor"
            }), " — Can create and edit models and syncs, but changes require approval before publishing."]
          }), "\n"]
        }), _jsxs(_components.p, {
          children: ["For more advanced setups, ", _jsx(_components.a, {
            href: "/workspace-management/roles#custom-roles",
            children: "create custom roles"
          }), " that include or exclude the ", _jsx(_components.code, {
            children: "Approve models & syncs"
          }), " permission on model and sync resources."]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "create-new-resources",
        children: "Create new resources"
      }), "\n", _jsx(_components.p, {
        children: "When you create a new model or sync, the available actions depend on your role:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Publish now"
          }), " publishes the resource immediately. Only available to roles that can bypass approval."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Submit approval request"
          }), " sends the draft to one or more approvers for review. You can include a comment."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Save as draft"
          }), " saves the resource without notifying anyone. You can keep editing and submit it for approval later."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/permissions/approvals-publish-now.png",
          alt: "Privileged role dialog showing Publish now, Submit approval request, and Save as draft options"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["If your role requires approval, you do not see ", _jsx(_components.strong, {
          children: "Publish now"
        }), ". You can only submit the resource for approval or save it as a draft."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/permissions/model-requires-approval.png",
          alt: "Workspace draft editor dialog showing Submit approval request and Save as draft options"
        })
      }), "\n", _jsx(_components.p, {
        children: "If a sync uses a model that is still in draft mode, both the model and the sync must be published before the sync can run."
      }), "\n", _jsx(_components.h2, {
        id: "edit-existing-resources",
        children: "Edit existing resources"
      }), "\n", _jsxs(_components.p, {
        children: ["Editing existing models and syncs uses the same approval options as ", _jsx(_components.a, {
          href: "#create-new-resources",
          children: "creating new resources"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "The following changes require approval:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Resource"
            }), _jsx(_components.th, {
              children: "Changes requiring approval"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Model"
            }), _jsx(_components.td, {
              children: "Query, configuration, and column type changes"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Sync"
            }), _jsx(_components.td, {
              children: "Configuration and schedule changes"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "If a sync uses a model with unpublished changes, the sync refers to the draft version of the model instead of the published version. Both the model and the sync must be published before the sync can run."
      }), "\n", _jsx(_components.h2, {
        id: "approve-requests",
        children: "Approve requests"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "#who-can-approve-requests",
          children: "Approvers"
        }), " can view approval requests by selecting ", _jsx(_components.strong, {
          children: "View request"
        }), " on the corresponding resource."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/permissions/view-approval-request.png",
          alt: "Pending approval banner with View request button on a model"
        })
      }), "\n", _jsx(_components.p, {
        children: "When reviewing a request, approvers can choose one of the following actions:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Approve & publish"
          }), " publishes the new resource or drafted changes."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Delete draft"
          }), " deletes the draft. New resources or drafted changes are discarded and cannot be recovered."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Withdraw request"
          }), " removes the approval request but keeps the draft. Users can continue editing and submit it again later."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/permissions/approve-deny.png",
          alt: "Draft review showing the model query with Approve & publish and Deny options"
        })
      }), "\n", _jsx(_components.p, {
        children: "Any user with approval permission and access to the resource can approve the request, even if they were not selected as the reviewer."
      }), "\n", _jsx(_components.p, {
        children: "For sync changes, the approval request shows a comparison of changes to the sync configuration. For model changes, the approval request shows changes to the model query."
      }), "\n", _jsx(_components.h3, {
        id: "who-can-approve-requests",
        children: "Who can approve requests"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "Approve models & syncs"
        }), " ", _jsx(_components.a, {
          href: "/workspace-management/roles#custom-role-grant-reference",
          children: "permission"
        }), " controls who can approve requests. By default, all roles ", _jsxs(_components.a, {
          href: "/workspace-management/roles#approval-flows",
          children: ["except ", _jsx(_components.strong, {
            children: "Workspace draft editor"
          }), " and ", _jsx(_components.strong, {
            children: "Workspace viewer"
          })]
        }), " include this permission:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Admin"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Workspace editor"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Model + sync editor"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Sync editor"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Source admin"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Destination admin"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Audience editor"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["To grant or remove approval permissions for a ", _jsx(_components.a, {
          href: "/workspace-management/roles#custom-roles",
          children: "custom role"
        }), ", add or remove the ", _jsx(_components.code, {
          children: "Approve models & syncs"
        }), " permission for the relevant sources or destinations. Users with this permission can use ", _jsx(_components.strong, {
          children: "Approve & publish"
        }), ", ", _jsx(_components.strong, {
          children: "Delete draft"
        }), ", and ", _jsx(_components.strong, {
          children: "Withdraw request"
        }), " when reviewing a draft."]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
